/**
 * Country master seed for the salon-owner signup picker (AB#1516,
 * amended by CR-2026-05-05a).
 *
 * **TEMPORARY STUB** — to be replaced by `/api/v2/countries/active` when
 * AB#1518 (DB-driven master tables) ships under Epic AB#1512.
 *
 * Per founder direction (CR-2026-05-05a):
 *
 *   - **All non-banned countries are active.** Devanza focuses on UAE +
 *     Jordan as launch ICP, but salon owners from any other country can
 *     register. The previous "inactive / Coming soon" treatment from
 *     CR-2026-05-04b was reversed because it created friction without
 *     business benefit — non-launch countries are still real customers.
 *
 *   - **Sanctions banlist** stays in place — these countries are
 *     filtered OUT of the dropdown entirely (Stripe / OFAC / EU
 *     sectoral blocks make signup inoperable anyway). Israel was added
 *     to the banlist by founder direct on 2026-05-05.
 *
 *   - **Render order**: launch markets pinned to top (AE, JO), then
 *     everything else alphabetically by EN name. Search filters at
 *     render time so order matters only for the unfiltered scroll.
 *
 * Country names come from `i18n-iso-countries` (EN + AR labels mirror
 * Unicode CLDR — the canonical MSA reference for AR). Dial codes from
 * `libphonenumber-js`. Flag rendering: SVG via the `flag-icons` package
 * (`<span class="fi fi-{iso2_lowercase}">`) — Unicode flag emojis don't
 * render on Windows Segoe UI Emoji, which was the pre-CR-2026-05-05a
 * regression where users saw "AE" / "JO" letter pairs instead of flags.
 *
 * Bug:  AB#1513 / AB#1660 (related — auth shell language toggle)
 * PBI:  AB#1516 (this) — Booksy-parity country picker + segmented phone
 * Epic: AB#1512 (Multi-Country, Multi-Currency, Multi-Branch Foundation)
 * CR:   CR-2026-05-04b (founder-approved 2026-05-04 18:45 UTC)
 *       CR-2026-05-05a (founder-direct 2026-05-05 — banlist + drop-inactive
 *       + flag-rendering amendment)
 */

import { getCountryCallingCode, type CountryCode } from 'libphonenumber-js';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import arLocale from 'i18n-iso-countries/langs/ar.json';

countries.registerLocale(enLocale);
countries.registerLocale(arLocale);

/**
 * Wide type — any valid ISO-3166-1 alpha-2 code that survives the banlist
 * filter. The runtime list is derived from `i18n-iso-countries` so this is
 * just `CountryCode` from libphonenumber-js (a string union of all ISO2
 * codes the library recognizes).
 *
 * Was a narrow union (`'AE' | 'JO' | 'SA'`) before AB#1516.
 */
export type SupportedCountryIso = CountryCode;

export interface CountrySeed {
    iso2: SupportedCountryIso;
    /** Lowercase ISO2 — used as the `flag-icons` CSS class suffix (`fi fi-{xx}`). */
    iso2Lower: string;
    nameEn: string;
    nameAr: string;
    /** E.164 dial prefix, e.g. "+971". */
    dialCode: string;
    /**
     * @deprecated Kept on the type for one release for backwards-compat
     * with consumers that haven't migrated past CR-2026-05-04b. Always
     * `true` post CR-2026-05-05a — the inactive concept was dropped.
     * Will be removed once `auth.schema.ts` and `SignupForm.tsx` are
     * cleaned of the old `country_not_supported` superRefine branch.
     */
    active: boolean;
    /** When `true` this country is the picker's initial selection on first paint. */
    default: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// PO-maintained sanctions banlist (CR-2026-05-04b + CR-2026-05-05a).
//
// REMOVED from the dropdown entirely — neither a user nor a developer should
// see them as a selectable option. Modifiable by PO via this constant; will
// move to AB#1518 master when the DB ships.
//
// IL added 2026-05-05 by founder Alaa direct (CR-2026-05-05a).
// ─────────────────────────────────────────────────────────────────────────────
export const SANCTIONS_BANLIST: readonly CountryCode[] = [
    'IR', // Iran — comprehensive sanctions
    'KP', // North Korea — comprehensive sanctions
    'SY', // Syria — comprehensive sanctions
    'CU', // Cuba — comprehensive sanctions
    'RU', // Russia — sectoral sanctions (post-2022)
    'BY', // Belarus — sectoral sanctions
    'VE', // Venezuela — sectoral sanctions
    'MM', // Myanmar — sectoral sanctions (Burma)
    'AF', // Afghanistan — Taliban-controlled regime
    'IL', // Israel — founder direct, CR-2026-05-05a
] as const;

// ─────────────────────────────────────────────────────────────────────────────
// Launch markets — pinned to the top of the dropdown's render order.
// All countries are active for signup post CR-2026-05-05a; this list is
// purely a sorting hint, not a gating decision.
// ─────────────────────────────────────────────────────────────────────────────
const LAUNCH_PRIORITY_ISO: readonly CountryCode[] = ['AE', 'JO'] as const;

/** Default country shown on first paint until GeoIP resolves. */
const DEFAULT_ISO: CountryCode = 'AE';

/**
 * Local Arabic name overrides for cases where the CLDR/i18n-iso-countries
 * default conflicts with Devanza's editorial preference. Empty for now —
 * the CLDR names match what arabic-translator recommends for the launch
 * markets. Add entries here if the founder or AR reviewer overrides a name.
 */
const AR_LABEL_OVERRIDES: Partial<Record<CountryCode, string>> = {};

function buildSeed(): readonly CountrySeed[] {
    const allIso2 = Object.keys(countries.getAlpha2Codes()) as CountryCode[];
    const ban = new Set<CountryCode>(SANCTIONS_BANLIST);
    const launch = new Set<CountryCode>(LAUNCH_PRIORITY_ISO);

    const survivors = allIso2.filter((iso) => !ban.has(iso));

    const items: CountrySeed[] = [];
    for (const iso of survivors) {
        let dialCode = '';
        try {
            dialCode = `+${getCountryCallingCode(iso)}`;
        } catch {
            // libphonenumber-js doesn't have a dial code for this ISO
            // (very rare; e.g. some sub-territories). Skip — no dial code
            // = unusable at signup.
            continue;
        }
        const nameEn = countries.getName(iso, 'en') ?? iso;
        const nameAr =
            AR_LABEL_OVERRIDES[iso] ?? countries.getName(iso, 'ar') ?? nameEn;
        items.push({
            iso2: iso,
            iso2Lower: iso.toLowerCase(),
            nameEn,
            nameAr,
            dialCode,
            // CR-2026-05-05a: every non-banned country is active for signup.
            active: true,
            default: iso === DEFAULT_ISO,
        });
    }

    // Render order: launch markets (AE, JO) pinned to top, then everything
    // else alphabetical by EN name.
    const cmp = (a: CountrySeed, b: CountrySeed) =>
        a.nameEn.localeCompare(b.nameEn);
    const isLaunch = (c: CountrySeed) => launch.has(c.iso2);

    return [
        ...items.filter(isLaunch).sort(cmp),
        ...items.filter((c) => !isLaunch(c)).sort(cmp),
    ];
}

export const COUNTRY_SEED: readonly CountrySeed[] = buildSeed();

export function getDefaultCountry(): CountrySeed {
    return (
        COUNTRY_SEED.find((c) => c.default) ?? COUNTRY_SEED[0]
    );
}

export function findCountry(
    iso2: SupportedCountryIso,
): CountrySeed | undefined {
    return COUNTRY_SEED.find((c) => c.iso2 === iso2);
}

/** Cast helper for libphonenumber-js APIs that accept a `CountryCode`. */
export function asCountryCode(iso2: SupportedCountryIso): CountryCode {
    return iso2 as CountryCode;
}

/**
 * AC-12 (CR-2026-05-04b) — when GeoIP returns a sanctions-banned country,
 * silently default to UAE. Does NOT surface the banlist to the user
 * (avoids brand risk + prevents banlist enumeration). All non-banned
 * countries are now active for signup (CR-2026-05-05a) so any survivor
 * resolves to itself.
 */
export function geoipCountryWithFallback(
    detected: string | null | undefined,
): SupportedCountryIso {
    if (!detected) return DEFAULT_ISO;
    const iso = detected.toUpperCase() as CountryCode;
    if ((SANCTIONS_BANLIST as readonly string[]).includes(iso)) {
        return DEFAULT_ISO;
    }
    if (findCountry(iso)) return iso;
    return DEFAULT_ISO;
}
